import React, { Suspense, lazy, Component } from "react";
import { Button, Flex } from "@appsmith/ads";
import { get } from "lodash";
import ErrorBoundary from "components/editorComponents/ErrorBoundry";
import AppsmithLogo from "assets/images/appsmith_logo_square.png";
import {
  getActions,
  getDatasource,
  getPlugins,
} from "ee/selectors/entitiesSelector";
import { fetchAccessToken } from "./RagApiRequests";
import { connect } from "react-redux";
import { getAppsmithConfigs } from "ee/configs";
import type { DefaultRootState } from "react-redux";
import type {
  Integration,
  EmbeddingGenerators,
  FunctionCallingIntegration,
} from "@appsmith/carbon-connect";
import "./carbon-modal.css";
import { IntegrationName } from "@appsmith/carbon-connect";
import type { Plugin } from "entities/Plugin";
import {
  createDatasourceAndSearchQueriesForAiAgent,
  openCarbonModal,
} from "ee/actions/aiAgentActions";
import { getSelectedIntegrationType } from "ee/selectors/aiAgentSelectors";
import store from "store";
import { setPluginActionEditorDebuggerState } from "PluginActionEditor/store/pluginActionEditorActions";
import { validateApiPath } from "@appsmith/utils";
import { generateAIAgentSchema } from "ee/actions/generateSchemaActions";
import type { ActionData } from "ee/reducers/entityReducers/actionsReducer";

const CarbonConnect = lazy(async () => import("./CarbonConnect"));
const { carbonApiBasePath } = getAppsmithConfigs();

const DEFAULT_ACTION_NAME = "ChatQuery";

interface CarbonButtonProps {
  datasourceId: string;
  workspaceId: string;
  integrations?: Integration[];
  embeddingModel?: EmbeddingGenerators;
  onModalStateChange?: (value: boolean) => void;
  plugins?: Plugin[];
  createDatasourceAndSearchQueriesForAiAgent?: typeof createDatasourceAndSearchQueriesForAiAgent;
  installingIntegrationName?: IntegrationName | null;
  isCarbonModalOpen?: boolean;
  openCarbonModal?: typeof openCarbonModal;
  selectedIntegrationType?: DefaultRootState["entities"]["aiAgents"]["selectedIntegrationType"];
  action?: ActionData;
}

const loadingError = <div>Failed to load CarbonConnect</div>;

const fallbackButton = (
  <Button isLoading size="md" startIcon="add-more">
    Add data
  </Button>
);

interface CarbonButtonState {
  isIntegrationsLoading: boolean;
}

const FUNCTION_CALLING_INTEGRATIONS: FunctionCallingIntegration[] = [
  {
    id: IntegrationName.ZENDESK,
    name: "Zendesk",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "zendesk",
        }),
      );
    },
  },
  {
    id: IntegrationName.SALESFORCE,
    name: "Salesforce",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "salesforce",
        }),
      );
    },
  },
  {
    id: IntegrationName.SLACK,
    name: "Slack",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "slack",
        }),
      );
    },
  },
  {
    id: IntegrationName.JIRA,
    name: "Jira",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "jira",
        }),
      );
    },
  },
  {
    id: IntegrationName.NOTION,
    name: "Notion",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "notion",
        }),
      );
    },
  },
  {
    id: IntegrationName.ASANA,
    name: "Asana",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "asana",
        }),
      );
    },
  },
  {
    id: IntegrationName.GMAIL,
    name: "Gmail",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "gmail",
        }),
      );
    },
  },
  {
    id: IntegrationName.GOOGLE_DOCS,
    name: "Google Docs",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "googledocs",
        }),
      );
    },
  },
  {
    id: IntegrationName.STRIPE,
    name: "Stripe",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "stripe",
        }),
      );
    },
  },
  {
    id: IntegrationName.GITHUB,
    name: "GitHub",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "github",
        }),
      );
    },
  },
  {
    id: IntegrationName.CONFLUENCE,
    name: "Confluence",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "confluence",
        }),
      );
    },
  },
  {
    id: IntegrationName.OUTLOOK,
    name: "Outlook",
    onClick() {
      store.dispatch(
        createDatasourceAndSearchQueriesForAiAgent({
          integrationName: this.id,
          pluginName: "outlook",
        }),
      );
    },
  },
];

class _CarbonButton extends Component<CarbonButtonProps, CarbonButtonState> {
  state = {
    isIntegrationsLoading: false,
    isCarbonModalOpen: false,
  };

  private onCarbonModalOpen = async () => {
    const { onModalStateChange } = this.props;

    this.props.openCarbonModal?.({
      shouldOpen: true,
      integrationType: "function-calling",
    });

    onModalStateChange && onModalStateChange(true);
  };

  render() {
    const {
      datasourceId,
      embeddingModel,
      onModalStateChange,
      selectedIntegrationType,
      workspaceId,
    } = this.props;

    validateApiPath(carbonApiBasePath || "");

    return (
      <ErrorBoundary fallback={loadingError}>
        <Suspense fallback={fallbackButton}>
          <Flex alignItems="center" gap="spaces-3">
            <Button
              isLoading={this.state.isIntegrationsLoading}
              kind="secondary"
              onClick={this.onCarbonModalOpen}
              size="md"
            >
              Add sources
            </Button>
          </Flex>
          {this.props.isCarbonModalOpen && (
            <CarbonConnect
              apiURL={carbonApiBasePath}
              backButtonText="Close"
              brandIcon={AppsmithLogo}
              embeddingModel={embeddingModel}
              enabledFCIntegrations={FUNCTION_CALLING_INTEGRATIONS}
              enabledIntegrations={this.props.integrations}
              entryPoint="INTEGRATION_LIST"
              installingIntegrationId={this.props.installingIntegrationName}
              onSuccess={() => {
                store.dispatch(
                  setPluginActionEditorDebuggerState({
                    open: true,
                    responseTabHeight: 300,
                  }),
                );

                store.dispatch(
                  generateAIAgentSchema(this.props.action?.config.id ?? ""),
                );
              }}
              open={this.props.isCarbonModalOpen}
              orgName="Appsmith"
              selectedIntegrationType={selectedIntegrationType}
              setOpen={(isOpen) => {
                this.props.openCarbonModal?.({ shouldOpen: isOpen as boolean });

                if (onModalStateChange) {
                  onModalStateChange(isOpen as boolean);
                }
              }}
              theme="light"
              tokenFetcher={async () =>
                fetchAccessToken(datasourceId, workspaceId)
              }
            />
          )}
        </Suspense>
      </ErrorBoundary>
    );
  }
}

export const CarbonButton = connect(
  (state: DefaultRootState, ownProps: CarbonButtonProps) => {
    const datasource = getDatasource(state, ownProps.datasourceId);
    const plugins = getPlugins(state);

    const integrations = get(
      datasource,
      `datasourceStorages.${state.environments.currentEnvironmentDetails.id}.datasourceConfiguration.properties[1].value`,
    ) as unknown as Integration[];

    const embeddingModel = get(
      datasource,
      `datasourceStorages.${state.environments.currentEnvironmentDetails.id}.datasourceConfiguration.embeddingModel`,
    ) as unknown as EmbeddingGenerators;

    const installingIntegrationName =
      state.entities.aiAgents.installingIntegrationName;
    const selectedIntegrationType = getSelectedIntegrationType(state);
    const isCarbonModalOpen = state.entities.aiAgents.isCarbonModalOpen;
    const actions = getActions(state);

    const action = actions.find(
      (action) => action.config.name === DEFAULT_ACTION_NAME,
    );

    return {
      integrations,
      embeddingModel,
      plugins,
      installingIntegrationName,
      isCarbonModalOpen,
      selectedIntegrationType,
      action,
    };
  },
  {
    createDatasourceAndSearchQueriesForAiAgent,
    openCarbonModal,
  },
)(_CarbonButton);
